import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Input, Modal, Select, Table, message } from 'antd';
import dayjs from 'dayjs';
import { Download, Plus, Search } from 'lucide-react';
import Layout from '../../Layout';
import { hrApi, useHr } from './hrStore';
import {
  OUTCOME, SOURCE, STAGE, STAGES, branchById, daysBetween, normText, positionById, reqById, toDay, type Candidate, type Source, type Stage,
  CANDIDATE_RETENTION_DAYS, interviewOverdue, needsSchedule, pendingInterview, retentionDue,
} from './hrLogic';
import { Chip, PageHeader, StageTag, exportXlsx, fmtDateTime, useHrPerms } from './hrUi';
import { CandidateModal } from './hrModals';
import CandidateDrawer from './CandidateDrawer';

/** Toàn bộ ứng viên: lọc theo bước, kết quả, nguồn; mở hồ sơ để chuyển bước / offer / nhận việc */
export default function Candidates({ openNew = false }: { openNew?: boolean }) {
  const s = useHr();
  const [params, setParams] = useSearchParams();
  const { canRecruit } = useHrPerms();
  const today = toDay();
  const stage = (params.get('stage') as Stage | 'closed' | 'all' | null) || 'open';
  const q = params.get('q') || '';
  const reqId = params.get('req') || '';
  const source = (params.get('source') as Source) || '';
  const id = params.get('id');
  const [adding, setAdding] = useState(openNew || !!params.get('new'));
  const go = (p: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const scoped = s.candidates.filter(c => (!reqId || c.requisitionId === reqId) && (!source || c.source === source)
    && (!q || normText(`${c.name} ${c.phone}`).includes(normText(q))));
  const matchStage = (c: Candidate, k: string) => (k === 'all' ? true : k === 'open' ? c.outcome === 'open' : k === 'closed' ? c.outcome === 'rejected' || c.outcome === 'withdrawn' : k === 'hired' ? c.outcome === 'hired' : c.outcome === 'open' && c.stage === k);
  const list = scoped.filter(c => matchStage(c, stage)).sort((a, b) => b.appliedAt - a.appliedAt);
  const reqLabel = (c: Candidate) => {
    const r = reqById(s, c.requisitionId);
    return r ? `${positionById(s, r.positionId)?.name} · ${branchById(s, r.branchId)?.short}` : '';
  };
  const due = retentionDue(s, today);
  const purge = () => Modal.confirm({
    title: `Xóa dữ liệu cá nhân ${due.length} hồ sơ quá hạn lưu trữ?`,
    content: `Hồ sơ không trúng tuyển đã đóng hơn ${CANDIDATE_RETENTION_DAYS} ngày. Xóa họ tên, liên hệ, CCCD, nhận xét; giữ nguồn, bước, lý do để thống kê. Không khôi phục được.`,
    okText: 'Xóa dữ liệu', okButtonProps: { danger: true }, cancelText: 'Thôi',
    onOk: () => { const r = hrApi.purgeExpiredCandidates(); if (r.ok) message.success(`Đã xóa dữ liệu ${r.id} hồ sơ`); },
  });
  const doExport = () => exportXlsx('ung_vien', 'Ứng viên', list.map(c => ({
    'Họ tên': c.name, 'Điện thoại': c.phone, 'Vị trí': reqLabel(c), 'Mã yêu cầu': reqById(s, c.requisitionId)?.code || '', 'Nguồn': SOURCE[c.source],
    'Ngày ứng tuyển': fmtDateTime(c.appliedAt), 'Bước': STAGE[c.stage].label, 'Kết quả': OUTCOME[c.outcome].label, 'Lý do': c.closeReason || '',
  })));

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Ứng viên" desc="Kiểm trùng theo SĐT / CCCD với ứng viên đang xử lý, nhân viên hiện tại và danh sách không tái tuyển"
          extra={<>
            <Button icon={<Download size={14} />} onClick={doExport}>Xuất Excel</Button>
            {canRecruit && <Button type="primary" icon={<Plus size={14} />} onClick={() => setAdding(true)}>Thêm ứng viên</Button>}
          </>} />
        {due.length > 0 && canRecruit && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
            <span><b>{due.length} hồ sơ</b> không trúng tuyển đã quá {CANDIDATE_RETENTION_DAYS / 365 * 12} tháng — cần xóa dữ liệu cá nhân theo Nghị định 13/2023.</span>
            <Button size="small" danger onClick={purge}>Xóa dữ liệu</Button>
          </div>
        )}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-sm space-y-3">
          <div className="flex flex-wrap gap-2">
            <Input allowClear prefix={<Search size={14} className="text-gray-400" />} placeholder="Tên, số điện thoại" className="!w-[220px]" value={q} onChange={e => go({ q: e.target.value || null })} />
            <Select className="w-[300px]" value={reqId} onChange={v => go({ req: v || null })}
              options={[{ value: '', label: 'Tất cả yêu cầu' }, ...s.requisitions.filter(r => r.status === 'approved' || s.candidates.some(c => c.requisitionId === r.id && c.outcome === 'open')).map(r => ({ value: r.id, label: `${r.code} · ${positionById(s, r.positionId)?.name} · ${branchById(s, r.branchId)?.short}` }))]} />
            <Select className="w-[200px]" value={source} onChange={v => go({ source: v || null })} options={[{ value: '', label: 'Tất cả nguồn' }, ...(Object.keys(SOURCE) as Source[]).map(k => ({ value: k, label: SOURCE[k] }))]} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Chip active={stage === 'open'} onClick={() => go({ stage: null })}>Đang xử lý · {scoped.filter(c => matchStage(c, 'open')).length}</Chip>
            {STAGES.slice(0, 5).map(st => <Chip key={st} active={stage === st} tone="sky" onClick={() => go({ stage: st })}>{STAGE[st].short} · {scoped.filter(c => matchStage(c, st)).length}</Chip>)}
            <Chip active={stage === 'hired'} onClick={() => go({ stage: 'hired' })}>Đã nhận việc · {scoped.filter(c => c.outcome === 'hired').length}</Chip>
            <Chip active={stage === 'closed'} onClick={() => go({ stage: 'closed' })}>Loại / rút · {scoped.filter(c => matchStage(c, 'closed')).length}</Chip>
            <Chip active={stage === 'all'} onClick={() => go({ stage: 'all' })}>Tất cả · {scoped.length}</Chip>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">
          <Table size="middle" rowKey="id" dataSource={list} scroll={{ x: 980 }} pagination={{ pageSize: 20, showSizeChanger: false, showTotal: t => `${t} ứng viên` }}
            onRow={c => ({ onClick: () => go({ id: c.id }), className: 'cursor-pointer' })}
            columns={[
              { title: 'Ứng viên', key: 'name', width: 220, render: (_, c) => <div><div className="font-medium">{c.name}</div><div className="text-[11px] text-gray-400">{c.phone}</div></div> },
              { title: 'Vị trí', key: 'req', width: 230, render: (_, c) => <div className="text-sm">{reqLabel(c)}<div className="text-[11px] text-gray-400">{reqById(s, c.requisitionId)?.code}</div></div> },
              { title: 'Bước / kết quả', key: 'stage', width: 200, render: (_, c) => {
                const iv = pendingInterview(c);
                return (
                  <div className="space-y-0.5">
                    <StageTag c={c} />
                    {c.closeReason && <div className="text-[11px] text-gray-400">{c.closeReason}</div>}
                    {iv && <div className={`text-[11px] ${interviewOverdue(iv) ? 'text-rose-600' : 'text-sky-700'}`}>{interviewOverdue(iv) ? 'Quá giờ PV, chưa ghi kết quả' : 'Hẹn PV'} {dayjs(iv.at).format('HH:mm DD/MM')}</div>}
                    {needsSchedule(c) && <div className="text-[11px] text-amber-600">Chưa hẹn lịch phỏng vấn</div>}
                  </div>
                );
              } },
              { title: 'Điểm', key: 'score', width: 80, render: (_, c) => { const sc = [...c.history].reverse().find(h => h.score)?.score; return sc ? <span className={sc >= 7 ? 'text-emerald-600 font-semibold' : sc >= 5 ? 'text-amber-600' : 'text-rose-600'}>{sc}/10</span> : <span className="text-gray-300">—</span>; } },
              { title: 'Nguồn', key: 'src', width: 160, render: (_, c) => <span className="text-gray-600 dark:text-gray-300">{SOURCE[c.source]}</span> },
              { title: 'Ứng tuyển', key: 'at', width: 150, render: (_, c) => {
                const wait = daysBetween(toDay(c.history[c.history.length - 1].at), today);
                return <div className="text-xs">{fmtDateTime(c.appliedAt)}{c.outcome === 'open' && <div className={wait > 3 ? 'text-rose-600' : 'text-gray-400'}>{wait ? `chờ ${wait} ngày ở bước này` : 'cập nhật hôm nay'}</div>}</div>;
              } },
            ]} />
        </div>
      </div>
      <CandidateDrawer id={id} onClose={() => go({ id: null })} />
      {adding && <CandidateModal reqId={reqId || undefined} onClose={() => { setAdding(false); if (params.get('new')) go({ new: null }); }} onSaved={cid => go({ id: cid })} />}
    </Layout>
  );
}
